import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import style from './state-diversification-table.scss?inline';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './state-diversification-table.constants.js';
import type { StateDiversificationTableContentObject } from './state-diversification-table.model.js';
import {
  formatToNDecimalPlaces,
  formatNumberToShowSign,
} from '../../common/utils/formatNumber.js';
import { IconSVG } from '../../common/utils/icon';
import { SortDirection, SortDirections } from '../../common/utils/tableSort';

/**
 * @tag pop-state-diversification-table-component
 * @attribute { StateDiversificationTableContentObject } [data-component-object] StateDiversificationTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopStateDiversificationTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * StateDiversificationTable content object
   */
  componentObject?: StateDiversificationTableContentObject;

  /**
   * Dispatches a custom holdings sort event with the id of the column to sort by
   *
   * @param column The id of the column to sort by
   */
  private dispatchSort(column: string) {
    this.dispatchEvent(
      new CustomEvent(Constants.SORT_EVENT, {
        detail: {
          column,
        },
      }),
    );
  }

  /**
   * Takes a sort direction and returns a human readable label to be used for accessibility purposes
   * Default is for the sort button that doesn't show a current sorting direction
   *
   * @param direction The sort direction
   * @returns A human readable label to be used for accessibility purposes
   */
  private getSortButtonLabel(direction?: SortDirection) {
    switch (direction) {
      case SortDirections.ASCENDING:
        return Constants.ASCENDING_LABEL;
      case SortDirections.DESCENDING:
        return Constants.DESCENDING_LABEL;
      default:
        return Constants.SORT_LABEL;
    }
  }

  /**
   * Renders the correct sort button for the specified column and current sort state
   *
   * @param obj Includes the column the button is for and the current sortState so it can
   * determine the correct icon to display
   *
   * @returns A sort button with the correct icon, accessible label, and click handler to
   * handle the sort event for the specified column
   */
  private renderSortButton({
    column,
    sortState,
  }: {
    column: string;
    sortState?: { column: string; direction: SortDirection };
  }) {
    const direction =
      sortState?.column === column ? sortState.direction : undefined;
    const sortButtonLabel = this.getSortButtonLabel(direction);

    return html`<beacon-icon-button
      variant="tertiary"
      slot="action-zone"
      @beacon-event-click="${() => {
        this.dispatchSort(column);
      }}"
      >${sortButtonLabel}
      ${this.renderSortButtonSVG(direction)}</beacon-icon-button
    >`;
  }

  /**
   * Renders the correct icon given the sort direction to show
   *
   * @param direction The sort direction to show
   */
  private renderSortButtonSVG(direction?: SortDirection) {
    switch (direction) {
      case SortDirections.ASCENDING:
        //Needs to be update once the icon is added to the beacon icon library
        return IconSVG.sortedAscending({ slot: 'icon' });
      case SortDirections.DESCENDING:
        //Needs to be update once the icon is added to the beacon icon library
        return IconSVG.sortedDescending({ slot: 'icon' });
      default:
        return html`<beacon-icon slot="icon" name="sort"></beacon-icon>`;
    }
  }

  private renderTableHead() {
    const ariaSort =
      this.componentObject?.sortState?.direction === SortDirections.ASCENDING
        ? Constants.ASCENDING_LABEL
        : Constants.DESCENDING_LABEL;

    return html`
      <tr>
        <th id="state" scope="col" aria-sort=${ifDefined(ariaSort)}>
          <beacon-table-header variant="normal">
            ${Constants.STATE_COLUMN_HEADER}
            ${this.renderSortButton({
              column: Constants.STATE_COLUMN_ID,
              sortState: this.componentObject?.sortState,
            })}
          </beacon-table-header>
        </th>
        <th
          id="percent-of-portfolio"
          scope="col"
          aria-sort=${ifDefined(ariaSort)}
          class="right-align"
        >
          <beacon-table-header variant="normal">
            ${Constants.PERCENT_OF_PORTFOLIO_COLUMN_HEADER}
            ${this.renderSortButton({
              column: Constants.PERCENT_OF_PORTFOLIO_COLUMN_ID,
              sortState: this.componentObject?.sortState,
            })}
          </beacon-table-header>
        </th>
        <th
          id="percent-change"
          scope="col"
          aria-sort=${ifDefined(ariaSort)}
          class="right-align"
        >
          <beacon-table-header variant="normal">
            ${Constants.PERCENT_CHANGE_FROM_LAST_PERIOD_COLUMN_HEADER}
            ${this.renderSortButton({
              column: Constants.PERCENT_CHANGE_COLUMN_ID,
              sortState: this.componentObject?.sortState,
            })}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = this.componentObject?.itemList;
    const sortedColumn = this.componentObject?.sortState?.column;

    return html`
      ${itemList?.map((item) => {
        return html`
          <tr>
            <th>
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${item.state || Constants.EMPTY_CELL_VALUE}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record
                variant="numeral"
                ?sorted=${sortedColumn ===
                Constants.PERCENT_OF_PORTFOLIO_COLUMN_ID}
              >
                ${formatToNDecimalPlaces(item.percentTotalNetAssets, 2)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                ?sorted=${sortedColumn === Constants.PERCENT_CHANGE_COLUMN_ID}
              >
                ${formatNumberToShowSign(item.percentageChange)}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  render() {
    const itemList = this.componentObject?.itemList;

    if (!itemList || itemList.length === 0) {
      return html``;
    }

    return this.renderComponent(html`
      <section class="state-diversification-table-content">
        <beacon-table variant="fixed">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>
    `);
  }
}
